import clsx from "clsx";
import React from "react";
import {
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  ListItemButtonProps,
  Box,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import { useUserIsClient } from "../../hooks/useCurrentUser";
import { maybePluralize } from "../../utils/text";
import { CardAvatar } from "../card/CardAvatar";
import { RecipientsDto } from "@growth-machine-llc/stridist-api-client";
import { RadioIndicator } from "../settings/coach-client-profile/RadioIndicator";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2.5, 1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    pointerEvents: "auto",
  },

  content: {
    display: "flex",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
  },

  itemAvatar: {
    minWidth: theme.spacing(6),

    [theme.breakpoints.up("md")]: {
      minWidth: theme.spacing(7),
    },
  },

  avatar: {
    margin: theme.spacing(0, 0, 0, 0),
    border: "2px solid",
    borderColor: theme.palette.grey[50],
    width: theme.spacing(4.5),
    height: theme.spacing(4.5),
    fontSize: 12,
    fontWeight: "bold",
    backgroundColor: theme.palette.avatar,

    [theme.breakpoints.up("md")]: {
      width: theme.spacing(7),
      height: theme.spacing(7),
      margin: theme.spacing(0, 2, 0, 0),
      fontSize: 18,
    },
  },

  primaryText: {
    color: theme.palette.secondary.main,
    fontWeight: 600,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [theme.breakpoints.up("md")]: {
      fontSize: 18,
    },
  },

  secondaryText: {
    color: theme.palette.text.secondary,
    wordBreak: "break-word",
    fontWeight: 500,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [theme.breakpoints.up("md")]: {
      fontSize: 16,
    },
  },

  selector: {
    pointerEvents: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: theme.spacing(1),
    cursor: "pointer",
  },
}));

export interface RecipientListItemProps extends ListItemButtonProps {
  recipient: RecipientsDto;
  selected?: boolean;
  onToggle?: () => void;
}

export function RecipientListItem({
  className,
  recipient,
  selected,
  onToggle,
  ...other
}: RecipientListItemProps) {
  const s = useStyles();
  const isClient = useUserIsClient();
  const { user: user, group: group } = recipient;
  const name = user?.displayName || group?.name || "";
  return (
    <ListItemButton
      className={clsx(s.root, className)}
      selected={false}
      onClick={onToggle}
      {...other}
    >
      <Box className={s.content}>
        <ListItemAvatar className={s.itemAvatar}>
          <CardAvatar
            className={clsx(s.avatar, className)}
            src={user?.photoUrl}
            children={name.substring(0, 1)}
          />
        </ListItemAvatar>

        <ListItemText
          classes={{
            primary: s.primaryText,
            secondary: s.secondaryText,
          }}
          primary={name}
          secondary={
            group
              ? maybePluralize(group.memberCount, "member")
              : !isClient && user?.email
          }
        />
      </Box>

      {onToggle && (
        <Box className={s.selector} onClick={onToggle}>
          <RadioIndicator selected={selected} />
        </Box>
      )}
    </ListItemButton>
  );
}
